import React, { useCallback, useMemo, useState } from "react";
import ReservationDateTimeSelectorV2 from "./ReservationDateTimeSelectorV2";
import {formatCategoryOptionLabel, sortCategoryOptions} from "../../config/categoryTariffs";
import {resolveWhiteLabelThemeId} from "../../config/whiteLabelThemes";
import {getPublicTextsAPI} from "../../network/publicCalls/GETPublicTexts";

const formatDateTimeForRentalPeriod = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    const hour = String(date.getHours()).padStart(2, "0");
    const minute = String(date.getMinutes()).padStart(2, "0");
    return `${year}-${month}-${day}T${hour}:${minute}`;
};

const DEFAULT_WEEKEND_START_MINUTES = 8 * 60;
const DEFAULT_WEEKEND_END_MINUTES = 8 * 60;
const DUTCH_WEEKDAYS = [
    { dayOfWeek: 1, label: "maandag" },
    { dayOfWeek: 2, label: "dinsdag" },
    { dayOfWeek: 3, label: "woensdag" },
    { dayOfWeek: 4, label: "donderdag" },
    { dayOfWeek: 5, label: "vrijdag" },
    { dayOfWeek: 6, label: "zaterdag" },
    { dayOfWeek: 7, label: "zondag" },
];

const formatMinutesToTime = (minutes) => {
    const safeMinutes = Math.max(0, Math.min((24 * 60) - 1, Number(minutes) || 0));
    const hour = String(Math.floor(safeMinutes / 60)).padStart(2, "0");
    const minute = String(safeMinutes % 60).padStart(2, "0");
    return `${hour}:${minute}`;
};

const getScheduleMinutesByType = (scheduleEntry, type) => {
    if (!scheduleEntry || scheduleEntry.isClosed || scheduleEntry.isOpen24h) return null;
    const hourKey = type === "open" ? "openHour" : "closeHour";
    const minuteKey = type === "open" ? "openMinute" : "closeMinute";
    const hour = Number(scheduleEntry?.[hourKey]);
    const minute = Number(scheduleEntry?.[minuteKey] ?? 0);
    if (!Number.isFinite(hour) || !Number.isFinite(minute)) return null;
    return (hour * 60) + minute;
};

const getWeekendTariffRangeText = (schedule) => {
    if (!Array.isArray(schedule) || schedule.length === 0) {
        return `zaterdag ${formatMinutesToTime(DEFAULT_WEEKEND_START_MINUTES)} tot maandag ${formatMinutesToTime(DEFAULT_WEEKEND_END_MINUTES)}`;
    }

    const saturdayEntry = schedule.find((entry) => Number(entry?.dayOfWeek) === 6);
    const mondayEntry = schedule.find((entry) => Number(entry?.dayOfWeek) === 1);

    const weekendStartMinutes = getScheduleMinutesByType(saturdayEntry, "open") ?? DEFAULT_WEEKEND_START_MINUTES;
    const weekendEndMinutes = getScheduleMinutesByType(mondayEntry, "open") ?? DEFAULT_WEEKEND_END_MINUTES;

    return `zaterdag ${formatMinutesToTime(weekendStartMinutes)} tot maandag ${formatMinutesToTime(weekendEndMinutes)}`;
};

const formatScheduleEntryRange = (scheduleEntry) => {
    if (!scheduleEntry || scheduleEntry.isClosed) return "gesloten";
    if (scheduleEntry.isOpen24h) return "24 uur geopend";

    const openMinutes = getScheduleMinutesByType(scheduleEntry, "open");
    const closeMinutes = getScheduleMinutesByType(scheduleEntry, "close");
    if (!Number.isFinite(openMinutes) || !Number.isFinite(closeMinutes)) return "gesloten";

    return `${formatMinutesToTime(openMinutes)}-${formatMinutesToTime(closeMinutes)}`;
};

const buildOpeningHoursRows = (schedule) => {
    const scheduleItems = Array.isArray(schedule) ? schedule : [];

    return DUTCH_WEEKDAYS.map((weekday) => {
        const scheduleEntry = scheduleItems.find((entry) => Number(entry?.dayOfWeek) === weekday.dayOfWeek);
        return {
            ...weekday,
            value: formatScheduleEntryRange(scheduleEntry),
        };
    });
};

const TERMS_OF_USE_LANGUAGES = ["nl", "en"];
const TERMS_OF_USE_KEYS = ["terms_of_use__main", "terms_of_use__footer"];

// /public/texts returns {nl: {key: html}, en: {...}}; empty subsidiaries return [] or {"": null}.
const pickTermsOfUseHtml = (texts) => {
    for (const language of TERMS_OF_USE_LANGUAGES) {
        const languageTexts = texts?.[language];
        if (!languageTexts || typeof languageTexts !== "object") continue;

        const html = TERMS_OF_USE_KEYS
            .map((key) => languageTexts[key])
            .filter((value) => typeof value === "string" && value.trim())
            .join("");
        if (html) return html;
    }

    return "";
};

export default function ReservationPanel({
    locationId,
    locationInfo,
    timeStepMinutes = 30,
    rentalPeriod = { from: "", to: "" },
    setRentalPeriod,
    expectedKm = "",
    setExpectedKm,
    showExpectedKmField = true,
    showDailyCategoryOptions = false,
    dailyCategoryOptions = [],
    selectedCategoryOptionId = null,
    onDailyCategoryOptionSelect = null,
    isDailyCategoryOptionLoading = false,
    dailyCategoryOptionLoadingId = null,
    highlightExpectedKm = false,
    minRentalText = "",
    showMinRentalWarning = false,
    tariffWarningText = "",
    minRentalMinutes = 0,
    useCurrentTimeDefault = false,
    isDailyRental = false,
    availabilityConflictText = "",
    pricePerExtraKilometer = null,
    eigenRisk = null,
    showPricesInclVat = true,
    subsidiaryId = null,
}) {
    const isPrivateShareTheme = resolveWhiteLabelThemeId() === "privateshare";
    const shouldShowExpectedKmField = showExpectedKmField || isPrivateShareTheme;
    const isDemoLocation = String(locationId || locationInfo?.id) === "875";
    const locationTitle = isDemoLocation
        ? "Demo Rent Amsterdam"
        : `${locationInfo.company.name} ${locationInfo.address.city}`;

    const [isDepositInfoOpen, setIsDepositInfoOpen] = useState(false);
    const [isOutsideScheduleWarningVisible, setIsOutsideScheduleWarningVisible] = useState(false);
    const [isOpeningHoursModalOpen, setIsOpeningHoursModalOpen] = useState(false);
    const [isTermsOfUseOpen, setIsTermsOfUseOpen] = useState(false);
    const [termsOfUseHtml, setTermsOfUseHtml] = useState("");
    const [termsOfUseStatus, setTermsOfUseStatus] = useState("idle");

    const openTermsOfUse = useCallback(async () => {
        setIsTermsOfUseOpen(true);
        if (termsOfUseStatus === "loading" || termsOfUseStatus === "ready") return;

        setTermsOfUseStatus("loading");
        try {
            const texts = await getPublicTextsAPI({subsidiaryId});
            setTermsOfUseHtml(pickTermsOfUseHtml(texts));
            setTermsOfUseStatus("ready");
        } catch (error) {
            console.warn("GET Public texts error", error);
            setTermsOfUseStatus("error");
        }
    }, [subsidiaryId, termsOfUseStatus]);

    const dailyExtraKmInfoText = useMemo(() => {
        const price = Number(pricePerExtraKilometer);
        if (!Number.isFinite(price)) return "";

        const cents = Math.max(0, Math.round(price * 100));
        const risk = Number(eigenRisk);
        const hasEigenRisk = Number.isFinite(risk) && risk > 0;

        if (hasEigenRisk) {
            return `De kosten voor een extra kilometer bedragen ${cents} cent, het eigen risico is ${Math.round(risk)} euro`;
        }

        return `De kosten voor een extra kilometer bedragen ${cents} cent`;
    }, [pricePerExtraKilometer, eigenRisk]);

    const weekendTariffRangeText = useMemo(
        () => getWeekendTariffRangeText(locationInfo?.schedule),
        [locationInfo?.schedule]
    );

    const openingHoursRows = useMemo(
        () => buildOpeningHoursRows(locationInfo?.schedule),
        [locationInfo?.schedule]
    );

    const normalizedDailyCategoryOptions = useMemo(() => {
        return sortCategoryOptions(dailyCategoryOptions, showPricesInclVat);
    }, [dailyCategoryOptions, showPricesInclVat]);

    const handleExpectedKmChange = (event) => {
        if (!setExpectedKm) return;
        const rawValue = event.target.value;
        const normalized = rawValue.replace(/[^\d]/g, "");
        setExpectedKm(normalized);
    };

    const selectorMinimumDuration = useMemo(
        () => (minRentalMinutes > 0 ? minRentalMinutes : 60),
        [minRentalMinutes]
    );

    const handlePeriodChange = useCallback(({ start, end }) => {
        if (typeof setRentalPeriod !== "function") return;
        setRentalPeriod({
            from: formatDateTimeForRentalPeriod(start),
            to: formatDateTimeForRentalPeriod(end),
        });
    }, [setRentalPeriod]);

    const handleOutsideScheduleChange = useCallback((isOutsideSchedule) => {
        setIsOutsideScheduleWarningVisible(Boolean(isOutsideSchedule));
    }, []);

    return (
        <div className="kav-panel">
            {/* WHERE */}
            <h2 className="kav-h2">Waar wil je huren?</h2>

            <div className="kav-locationCard">
                <div className="kav-checkIcon" aria-hidden="true">
                    ✓
                </div>

                <div className="kav-locationText">
                    <div className="kav-locationTitle">{locationTitle}</div>

                    <div className="kav-locationSub">
                        {/*{locationInfo.address.street} {locationInfo.address.houseNumber}, {locationInfo.address.zipCode} {locationInfo.address.city}*/}
                        {
                            locationInfo?.company?.logo
                                ? <img src={locationInfo?.company?.logo} style={{maxWidth: "120px", height: "auto"}} alt=""/>
                                : ""
                        }
                    </div>
                </div>
            </div>

            <hr className="kav-sep"/>

            {/* PERIOD */}
            <h2 className="kav-h2">Selecteer een huurperiode</h2>

            {!showExpectedKmField && showDailyCategoryOptions && normalizedDailyCategoryOptions.length > 0 ? (
                <>
                    <div className="kav-daily-tariffs" style={{marginTop: "12px"}}>
                        {normalizedDailyCategoryOptions.map((categoryOption) => {
                            const optionId = Number(categoryOption?.id);
                            const isActive = Number(selectedCategoryOptionId) === optionId;
                            const isLoading = Boolean(
                                isDailyCategoryOptionLoading &&
                                dailyCategoryOptionLoadingId !== null &&
                                Number(dailyCategoryOptionLoadingId) === optionId
                            );
                            const isAnyTariffLoading = Boolean(
                                isDailyCategoryOptionLoading && dailyCategoryOptionLoadingId !== null
                            );

                            return (
                                <button
                                    key={`daily-category-option-${categoryOption.id}`}
                                    type="button"
                                    className={`kav-daily-tariff${isActive ? " is-active" : ""}${isLoading ? " is-loading" : ""}`}
                                    disabled={isAnyTariffLoading}
                                    onClick={() => {
                                        if (typeof onDailyCategoryOptionSelect === "function") {
                                            onDailyCategoryOptionSelect(categoryOption);
                                        }
                                    }}
                                >
                                    {isLoading ? (
                                        <>
                                            <span className="kav-spinner" aria-hidden="true"></span>
                                            Laden...
                                        </>
                                    ) : (
                                        formatCategoryOptionLabel(categoryOption, showPricesInclVat)
                                    )}
                                </button>
                            );
                        })}
                    </div>

                    {tariffWarningText ? (
                        <div className="kav-callout" style={{background: "#ffea2e", marginTop: "16px"}}>
                            <span>{tariffWarningText}</span>
                        </div>
                    ) : null}
                </>
            ) : null}

            <div className="kav-form">
                <ReservationDateTimeSelectorV2
                    startTimeStepMinutes={timeStepMinutes}
                    minimumDurationMinutes={selectorMinimumDuration}
                    initialStart={rentalPeriod.from}
                    initialEnd={rentalPeriod.to}
                    locationSchedule={locationInfo?.schedule}
                    locationWebsite={locationInfo?.website}
                    useCurrentTimeDefault={useCurrentTimeDefault}
                    isDailyRental={isDailyRental}
                    onPeriodChange={handlePeriodChange}
                    onOutsideScheduleChange={handleOutsideScheduleChange}
                >
                    {shouldShowExpectedKmField ? (
                        <div className={`kav-field kav-field--full ${highlightExpectedKm ? "kav-field--error" : ""}`}
                             style={{marginTop: "12px"}}>
                            <span className="kav-field__icon" aria-hidden="true">
                                <i className="bi bi-speedometer2"></i>
                            </span>
                            <span className="kav-field__content">
                                <label className="kav-field__label" htmlFor="expected-km">
                                    Geschatte afstand (VERPLICHT) invoeren
                                </label>
                                <input
                                    id="expected-km"
                                    className="kav-input"
                                    type="text"
                                    inputMode="numeric"
                                    pattern="[0-9]*"
                                    value={expectedKm}
                                    onChange={handleExpectedKmChange}
                                    placeholder="0"
                                    required={isPrivateShareTheme}
                                />
                            </span>
                        </div>
                    ) : null}
                </ReservationDateTimeSelectorV2>

                {isDailyRental && isOutsideScheduleWarningVisible ? (
                    <div className="kav-rdt2__outsideHoursNotice">
                        De door u gewenste reserveringstijd valt buiten onze openingstijden. Bel ons gerust op{" "}
                        <a href="tel:0880883434">088 088 34 34</a>, dan helpen wij u graag met een reservering buiten
                        openingstijden.
                        <div className="kav-outside-hours-notice__actions">
                            <button
                                type="button"
                                className="kav-opening-hours-btn"
                                onClick={() => setIsOpeningHoursModalOpen(true)}
                            >
                                Openingstijden
                            </button>
                        </div>
                    </div>
                ) : null}

                {availabilityConflictText ? (
                    <div className="kav-availability-conflict-inline" role="status" aria-live="polite">
                        {availabilityConflictText}
                    </div>
                ) : null}
            </div>

            {/* GOOD TO KNOW */}
            <h3 className="kav-h3">Goed om te weten</h3>

            {useCurrentTimeDefault ? (
                <>
                    <ul className="kav-list">
                        <li>Brandstof en verzekering inbegrepen.</li>
                        <li>Geen eigen risico - geen aansprakelijkheid bij schade.</li>
                        <li>Geen verborgen kosten.</li>
                        <li>
                            De{" "}
                            <button
                                style={{color: "red !important"}}
                                type="button"
                                className="kav-inline-info-link"
                                onClick={() => setIsDepositInfoOpen(true)}
                            >
                                borg
                            </button>
                            {" "}
                            is afhankelijk van de huurduur (min. €300)
                            <button
                                type="button"
                                className="kav-inline-info-icon"
                                onClick={() => setIsDepositInfoOpen(true)}
                                aria-label="Meer informatie over borg"
                            >
                                <i className="bi bi-info-circle"></i>
                            </button>
                        </li>
                        <li>
                            <button
                                type="button"
                                className="kav-inline-info-link"
                                onClick={openTermsOfUse}
                            >
                                Terms of use
                            </button>
                        </li>
                    </ul>

                    {isTermsOfUseOpen && (
                        <div className="kav-simple-modal" role="dialog" aria-modal="true">
                            <button
                                type="button"
                                className="kav-simple-modal__backdrop"
                                onClick={() => setIsTermsOfUseOpen(false)}
                                aria-label="Sluiten"
                            />
                            <div className="kav-simple-modal__card kav-terms-modal">
                                <h3>Terms of use</h3>
                                {termsOfUseStatus === "ready" && termsOfUseHtml ? (
                                    <div
                                        className="kav-terms-modal__content"
                                        dangerouslySetInnerHTML={{__html: termsOfUseHtml}}
                                    />
                                ) : (
                                    <p>
                                        {termsOfUseStatus === "loading"
                                            ? "Laden..."
                                            : termsOfUseStatus === "error"
                                                ? "De voorwaarden konden niet worden geladen. Probeer het later opnieuw."
                                                : "Er zijn geen voorwaarden beschikbaar."}
                                    </p>
                                )}
                                <div className="kav-auth-actions">
                                    <button
                                        type="button"
                                        className="kav-step-actions__btn kav-step-actions__btn--primary"
                                        onClick={() => setIsTermsOfUseOpen(false)}
                                    >
                                        Sluiten
                                    </button>
                                </div>
                            </div>
                        </div>
                    )}

                    {isDepositInfoOpen && (
                        <div className="kav-simple-modal" role="dialog" aria-modal="true">
                            <button
                                type="button"
                                className="kav-simple-modal__backdrop"
                                onClick={() => setIsDepositInfoOpen(false)}
                                aria-label="Sluiten"
                            />
                            <div className="kav-simple-modal__card">
                                <h3>Borginformatie</h3>
                                <p>
                                    Uw borg, verminderd met de ritkosten, wordt dezelfde dag terugbetaald,
                                    mits de parkeerregels niet zijn overtreden.
                                </p>
                                <div className="kav-auth-actions">
                                    <button
                                        type="button"
                                        className="kav-step-actions__btn kav-step-actions__btn--primary"
                                        onClick={() => setIsDepositInfoOpen(false)}
                                    >
                                        Sluiten
                                    </button>
                                </div>
                            </div>
                        </div>
                    )}
                </>
            ) : (
                <ul className="kav-list">
                    <li>De prijs is inclusief alle kosten: btw, verzekering en pechhulp.</li>
                    <li>{`Het weekendtarief geldt van ${weekendTariffRangeText}.`}</li>
                    <li>Het zaterdagtarief geldt alleen binnen de openingstijden van de locatie op zaterdag.</li>
                    {dailyExtraKmInfoText ? (
                        <li>{dailyExtraKmInfoText}</li>
                    ) : null}
                </ul>
            )}

            {isOpeningHoursModalOpen ? (
                <div className="kav-simple-modal" role="dialog" aria-modal="true" aria-labelledby="opening-hours-title">
                    <button
                        type="button"
                        className="kav-simple-modal__backdrop"
                        onClick={() => setIsOpeningHoursModalOpen(false)}
                        aria-label="Sluiten"
                    />
                    <div className="kav-simple-modal__card kav-opening-hours-modal">
                        <button
                            type="button"
                            className="kav-simple-modal__close"
                            onClick={() => setIsOpeningHoursModalOpen(false)}
                            aria-label="Sluiten"
                        >
                            <i className="bi bi-x-lg" aria-hidden="true"></i>
                        </button>

                        <h3 id="opening-hours-title" className="kav-opening-hours-modal__title">
                            Openingstijden
                        </h3>

                        <div className="kav-opening-hours-modal__list">
                            {openingHoursRows.map((row) => (
                                <div className="kav-opening-hours-modal__row" key={row.dayOfWeek}>
                                    <span className="kav-opening-hours-modal__day">{row.label}:</span>
                                    <strong className="kav-opening-hours-modal__time">{row.value}</strong>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            ) : null}
        </div>
    );
}
